import { Box, CircularProgress, Collapse, IconButton, Typography } from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import CustomDataGrid2 from '@src/components/CustomDataGrid/CustomDataGrid2';
import CustomDrawer from '@src/components/CustomDrawer/CustomDrawer';
import { DeleteIcon, LockIcon } from '@src/components/Icons';
import { IsMobileWidth, IsTabletWidth } from '@src/components/MediaQueryUtils';
import SearchBar from '@src/components/SearchBar';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import BranchCreation from './Components/Forms/BranchCreation';
import BranchEditable from './Components/Forms/BranchEditable';
import CustomModal from '@src/components/CustomModal';
import ConfirmModal from '@src/components/ConfirmModal/ConfirmModal';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { AxiosError, AxiosResponse } from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getBaseRoute } from '@src/helpers/Helpers';
import { setBranches, setPageNumber, setRestaurants } from '@src/store/reducers/branchReducer';
import { use } from 'i18next';
import { CustomAlertType } from '@src/interfaces/type';
import { RESTAURANT } from '@src/Constants';
import { adminUsers } from '@src/helpers/Permission';
import CustomPermission from '@src/components/Permissions/CustomPermission';
import PageHeaderMobile from '@src/components/PageHeader/PageHeaderMobile';
import { SelectedItemsTextContainer } from '@src/components/CommonComponents/Common';
import CustomTextButton from '@src/components/CustomButton/CustomTextButton';
import SettingsCard from '@src/components/CustomCard/SettingsCard';
import Loading from '@src/components/Loading/Loading';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	overflow: scroll;
	padding: 24px;
	gap: 24px;
	.container1 {
		display: flex;
		justify-content: space-between;
		align-items: center;
		p {
			color: var(--color-font-grey);
			font-size: 14px;
			font-weight: 600;
		}
	}
	@media screen and (max-width: 600px) {
		margin-bottom: 68px;
	}
`;
const SelectedItemsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 5px;
	font-size: 16px;
	font-weight: 750;
	line-height: 21.82px;
	transition: width 0.3s ease;
`;
interface MediaQueries {
	'@media (max-width: 600px)': React.CSSProperties;
}
const stylingDeleteModal: React.CSSProperties & MediaQueries = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '306px',
	height: '375px',
	backgroundColor: 'background.paper',
	borderRadius: '4px',
	boxShadow: '24px',
	'@media (max-width: 600px)': {
		width: '306px',
	},
};

//For Mobile View
const StyledBottomBtnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px;
	background: var(--chat-header-bg);
	border: 1.5px solid var(--gray-color-light);
	z-index: 10;
	flex-shrink: 0; // Prevents resizing when the content overflows
	position: absolute;
	bottom: 0px;
`;

const StyledMobileContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	overflow: hidden;
	width: 100%;
	#order-details {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 16px;
		flex-shrink: 0;
		.field {
			display: flex;
			gap: 6px;
			color: #5c5c5c;
			font-size: 14px;
			font-weight: 500;
			span {
				font-weight: 700;
			}
		}
	}
	#filter-container {
		display: flex;
		gap: 10px;
		padding: 16px;
	}
`;

const Container = styled.div({
	background: '#F1F1F1',
	paddingTop: '2px',
	overflowY: 'auto',
	flexGrow: 1, // Ensure it takes up remaining space and allows scrolling
	marginBottom: '235px',
});
type TimeoutHandler = ReturnType<typeof setTimeout> | null;
interface Props {
	setAlert: (data: CustomAlertType) => void;
	displayCustomError: any;
	setSearchValue: any;
	searchValue: any;
	rows: any;
	setRows: any;
	filteredRows: any;
	setFilteredRows: any;
}
const BranchSettings: React.FC<Props> = ({
	setAlert,
	displayCustomError,
	setSearchValue,
	searchValue,
	rows,
	setRows,
	filteredRows,
	setFilteredRows,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const allBranch: any = useAppSelector((state) => state?.restaurant?.allBranch);
	const pageNumber: any = useAppSelector((state) => state?.restaurant?.pageNumber);
	const restaurant: any = useAppSelector((state) => state?.restaurant?.value);
	const isRestaurant = currentUser?.type?.toLowerCase() === RESTAURANT;
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();

	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [openDrawer, setOpenDrawer] = useState(false);
	const [openEditDrawer, setOpenEditDrawer] = useState(false);
	const [deleteProductModal, setDeleteProductModal] = useState(false);
	const [loadingData, setLoadingData] = useState(true);
	const [loading, setLoading] = useState(false);
	const [isApiCall, setApiCall] = useState(false);
	const [operation, setOperation] = useState('');
	const [rowData, setRowData] = useState([]);
	const timeoutHandler = useRef<TimeoutHandler>(null);
	const [collapse, setCollapse] = useState(false);
	const isAdmin = adminUsers?.includes(currentUser?.userType);

	const [page, setPage] = useState(pageNumber);
	const [totalPages, setTotalPages] = useState(1);

	const isMobileWidth = IsMobileWidth();

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const columns = isRestaurant
		? [
				{ field: 'id', headerName: 'CRM Account ID', width: 140 },
				{ field: 'name', headerName: 'Account Name', width: 300 },
				{ field: 'branchId', headerName: 'Branch No', width: 180 },
				{ field: 'address', headerName: 'Address', width: 400 },
		  ]
		: [
				{ field: 'id', headerName: 'Supplier ID', width: 140 },
				{ field: 'name', headerName: 'Supplier Name', width: 300 },
				{ field: 'branchId', headerName: 'Branch No', width: 180 },
				{ field: 'address', headerName: 'Address', width: 400 },
		  ];

	const onRowsSelectionHandler = (ids: any) => {
		setSelectedItemIds(ids);
	};
	const handleRowClick = (params: any, event: any) => {
		setRowData(params?.row);
		setOpenEditDrawer(true);
	};

	const handleClick = () => {
		setOpenDrawer(true);
	};

	const deletingYourOwnBranch = () => {
		if (selectedItemIds?.includes(restaurant?.default?.id)) {
			navigate(`/${isRestaurant ? 'restaurants' : 'suppliers'}`);
		}
	};

	useEffect(() => {
		if (searchParams?.get('branchId') && filteredRows?.length) {
			const currentRow = filteredRows?.find(
				(row: any) => row?.id === Number(searchParams?.get('branchId'))
			);
			if (currentRow) {
				setRowData(currentRow);
				setOpenEditDrawer(true);
			}
		}
	}, [searchParams, filteredRows]);

	const handleConfirm = () => {
		setLoading(true);

		const deletePromises = selectedItemIds.map((id) => {
			return new Promise((resolve, reject) => {
				apiService.deleteBranch(
					(response: AxiosResponse) => {
						resolve(response);
					},
					(error: AxiosError) => {
						reject(error);
					},
					id
				);
			});
		});

		Promise.all(deletePromises)
			.then((responses) => {
				deletingYourOwnBranch();
				setLoading(false);
				setAlert({
					message: `${
						selectedItemIds?.length > 1
							? isRestaurant
								? 'Restaurants'
								: 'Suppliers'
							: isRestaurant
							? 'Dealer'
							: 'Supplier'
					} Deleted Successfully`,
					severity: 'success',
				});
				setSelectedItemIds([]);
				setApiCall(true);
				setDeleteProductModal(false);
			})
			.catch((errors) => {
				setLoading(false);
				displayCustomError('Something went wrong!');
			});
	};

	const handleSearch = (value: any) => {
		setSearchValue(value);
		const filteredRows: any = rows?.filter((row: any) => {
			const lowerCaseValue = value?.toLowerCase();

			return (
				row?.name?.toLowerCase()?.includes(lowerCaseValue) ||
				row?.id?.toString()?.includes(lowerCaseValue) ||
				row?.address?.toString()?.includes(lowerCaseValue)
			);
		});
		console.log(filteredRows);
		setFilteredRows(filteredRows);
	};

	const handleSelectAll = (products: any) => {
		const ids: string[] =
			selectedItemIds?.length === filteredRows?.length
				? []
				: filteredRows?.map((item: any) => item?.id);
		setRows((prevState: any) => {
			return prevState?.map((item: any) => {
				return {
					...item,
					isSelection: !(selectedItemIds?.length === filteredRows?.length),
				};
			});
		});
		setFilteredRows((prevState: any) => {
			return prevState?.map((item: any) => {
				return {
					...item,
					isSelection: !(selectedItemIds?.length === filteredRows?.length),
				};
			});
		});

		setSelectedItemIds(ids);
	};

	const toggleSelect = (orderId: string, e: any) => {
		if (isSelected(orderId)) {
			setSelectedItemIds(selectedItemIds.filter((id) => id !== orderId));
		} else {
			setSelectedItemIds([...selectedItemIds, orderId]);
		}
	};

	const handleCheckboxChange = (id: string, e: any) => {
		toggleSelect(id, e);
	};

	const viewDetails = (item: any, e?: any) => {
		if (e?.target?.tagName === 'INPUT') {
			return;
		}
		setRowData(item);
		setOpenEditDrawer(true);
	};

	const isSelected = (id: string) => selectedItemIds?.includes(id);

	const getAllBranches = () => {
		apiService?.getBranches(
			(response: AxiosResponse) => {
				const data = response?.data?.data?.map((branch: any) => {
					return { ...branch, branchId: branch?.phone };
				});
				const totalCount = data?.totalCount;
				const totalPages = Math.max(Math.ceil(totalCount / 10), 1);

				//todo: fix duplicate state
				dispatch(setBranches(data));
				dispatch(setRestaurants(data));

				setRows(data);
				setFilteredRows(data);
				setTotalPages(totalPages);
				setLoadingData(false);
				if (isApiCall) {
					setApiCall(false);
				}
			},
			(error: AxiosError) => {
				console.error(error);
				setLoadingData(false);
				displayCustomError('Something went wrong!');
			}
		);
	};

	useEffect(() => {
		//persisted data
		if (allBranch?.length) {
			setRows(allBranch);
			setFilteredRows(allBranch);
			setLoadingData(false);
		}
	}, []);

	useEffect(() => {
		if (isAdmin) {
			getAllBranches();
		} else {
			setLoadingData(false);
		}
	}, [isApiCall]);

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	const modals = () => {
		return (
			<>
				{openDrawer ? (
					<CustomDrawer
						open={openDrawer}
						setOpen={setOpenDrawer}
						style={{ width: windowDimensions?.width <= 800 ? '100%' : '613px' }}
						header={false}
					>
						<BranchCreation
							setApiCall={setApiCall}
							setAlert={setAlert}
							setOpenDrawer={setOpenDrawer}
							displayCustomError={displayCustomError}
							windowDimensions={windowDimensions}
						/>
					</CustomDrawer>
				) : null}
				{openEditDrawer ? (
					<CustomDrawer
						open={openEditDrawer}
						setOpen={setOpenEditDrawer}
						style={{ width: windowDimensions?.width <= 800 ? '100%' : '613px' }}
						header={false}
					>
						<BranchEditable
							setApiCall={setApiCall}
							setAlert={setAlert}
							rowData={rowData}
							setOpenDrawer={setOpenEditDrawer}
							displayCustomError={displayCustomError}
							windowDimensions={windowDimensions}
						/>
					</CustomDrawer>
				) : null}
				{deleteProductModal ? (
					<CustomModal
						open={deleteProductModal}
						setOpen={setDeleteProductModal}
						title={`Remove Branch`}
						style={stylingDeleteModal}
						children={
							<ConfirmModal
								content={`Deleting ${selectedItemIds?.length} ${
									selectedItemIds?.length > 1 ? 'branches' : 'branch'
								} . Are you sure?`}
								loading={false}
								handleClose={() => setDeleteProductModal(false)}
								handleConfirm={handleConfirm}
							/>
						}
					/>
				) : null}
			</>
		);
	};

	if (loadingData) {
		return <Loading />;
	}

	if (isMobileWidth) {
		return (
			<>
				{modals()}
				{adminUsers?.includes(currentUser?.userType) ? (
					<StyledMobileContainer>
						<div id="filter-container">
							<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
								{filteredRows?.length ? (
									<CustomTextButton
										style={{
											fontSize: '13px',
											fontWeight: 700,
											color: '#FF6120',
											textDecoration: 'none',
											maxWidth: '75px',
											marginTop: '4px',
										}}
										title={
											selectedItemIds?.length === filteredRows?.length
												? 'Deselect All'
												: 'Select All'
										}
										onClick={() => handleSelectAll(filteredRows)}
									/>
								) : null}
								<SelectedItemsTextContainer
									selectedItemIds={selectedItemIds}
									filteredItems={filteredRows}
									items
								/>
							</div>
						</div>
						<Container>
							{filteredRows?.map((item: any) => {
								return (
									<SettingsCard
										key={item?.id}
										item={item}
										avatarUrl={item?.image}
										isSelected={isSelected}
										handleCheckboxChange={handleCheckboxChange}
										viewDetails={viewDetails}
										isContainCheckbox
									/>
								);
							})}
						</Container>
						<StyledBottomBtnContainer>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: '100%',
								}}
							>
								{selectedItemIds?.length ? (
									<CustomButton
										title="Delete Item(s)"
										handleClick={() => setDeleteProductModal(true)}
										styles={{
											width: '320px',
											height: '40px',
											background: '#F26184',
											color: '#FFFFFF',
											transition: 'background 0.3s ease, color 0.3s ease',
										}}
									/>
								) : (
									<CustomButton
										title={`Create New Branch`}
										handleClick={handleClick}
										heightVariant="medium"
										color="primary"
										styles={{
											width: '328px',
											height: '40px',
											padding: '7px 12px',
											transition: 'background 0.3s ease, color 0.3s ease',
										}}
										disabled={!isRestaurant}
									/>
								)}
							</div>
						</StyledBottomBtnContainer>
					</StyledMobileContainer>
				) : (
					<CustomPermission
						icon={<LockIcon />}
						title="We are sorry..."
						paragraph={`You do not have permission to access branch settings. Contact 
				an administrator to get permission.`}
					/>
				)}
			</>
		);
	}
	return (
		<>
			{modals()}
			{adminUsers?.includes(currentUser?.userType) ? (
				<>
					<StyledContainer>
						<div className="container1">
							{isRestaurant ? (
								<p>Allow you to create new branch and manage existing branch information.</p>
							) : (
								<p>Allow you to create new branch and manage existing branch information.</p>
							)}
							{windowDimensions?.width > 800 ? (
								<CustomButton
									title={`Create New Branch`}
									handleClick={handleClick}
									heightVariant="medium"
									color="primary"
									styles={{
										width: '195px',
										height: '36px',
										padding: '7px 12px',
									}}
									disabled={!isRestaurant}
								/>
							) : null}
						</div>
						<div className="container1">
							<SearchBar
								style={{
									width: '328px',
									height: '36px',
								}}
								value={searchValue}
								onChange={handleSearch}
								isLoading={false}
							/>
							{selectedItemIds?.length ? (
								<SelectedItemsContainer>
									<Typography>{selectedItemIds?.length} items selected</Typography>
									<IconButton
										onClick={() => {
											setDeleteProductModal(true);
										}}
									>
										<DeleteIcon />
									</IconButton>
								</SelectedItemsContainer>
							) : null}
						</div>
						<CustomDataGrid2
							rows={filteredRows}
							columns={columns}
							onRowsSelectionHandler={onRowsSelectionHandler}
							handleRowClick={handleRowClick}
							style={{
								transition: 'height 0.3s ease',
								maxHeight: 'calc(100vh - 320px)',
								minHeight: 'calc(100vh - 320px)',
							}}
						/>
					</StyledContainer>
				</>
			) : (
				<CustomPermission
					icon={<LockIcon />}
					title="We are sorry..."
					paragraph={`You do not have permission to access ${
						currentUser?.type?.toLowerCase() === RESTAURANT ? 'dealer' : 'supplier'
					} settings. Contact 
				an administrator to get permission.`}
				/>
			)}
		</>
	);
};

export default BranchSettings;
